import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { GoogleLogin } from '@react-oauth/google';
import { useLocation, useNavigate } from 'react-router-dom';
import { config } from '../constants.js';
import { setRememberMeEligible } from '../infrastructure/authStorage.js';
import { usePaywall } from './PaywallProvider.js';
import { getSignInModalCopy } from './signInModalTitle.js';
import { getCheckoutReturnPath } from './learnMoreNavigation.js';
import {
  buildJoinAuthStateFromLocation,
  formatJoinCheckoutError,
  runJoinCheckoutAfterAuth,
} from './joinCheckoutFlow.js';
import './paywall.scss';

const url = config.url.API_URL;

/**
 * @param {object} props
 * @param {boolean} [props.isAuthenticated]
 * @param {Function} [props.setIsAuthenticated]
 * @param {Function} [props.setUserId]
 * @param {Function} [props.onLoginSuccess]
 */
export default function SignInModal({
  isAuthenticated = false,
  setIsAuthenticated,
  setUserId,
  onLoginSuccess,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { signInModalOpen, signInModalContext, closeSignInModal, refreshAccess } = usePaywall();
  const modalCopy = getSignInModalCopy(signInModalContext);

  const [joinMode, setJoinMode] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const joinModeRef = useRef(joinMode);
  joinModeRef.current = joinMode;

  useEffect(() => {
    if (!signInModalOpen) {
      setJoinMode(false);
      setCheckoutLoading(false);
      setCheckoutError(null);
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [signInModalOpen]);

  const startCheckoutFlow = useCallback(async () => {
    setCheckoutLoading(true);
    setCheckoutError(null);

    try {
      const returnTo = getCheckoutReturnPath(location);
      const result = await runJoinCheckoutAfterAuth({
        returnTo,
        refreshAccess,
      });

      if (result.redirecting) {
        return;
      }

      closeSignInModal();
      onLoginSuccess?.({ access: result.access });
    } catch (error) {
      setCheckoutError(formatJoinCheckoutError(error));
    } finally {
      setCheckoutLoading(false);
    }
  }, [closeSignInModal, location, onLoginSuccess, refreshAccess]);

  const navigateToEmailAuth = useCallback((path) => {
    closeSignInModal();
    navigate(path, { state: buildJoinAuthStateFromLocation(location) });
  }, [closeSignInModal, location, navigate]);

  const handleJoinClick = useCallback(() => {
    setCheckoutError(null);
    if (isAuthenticated) {
      startCheckoutFlow();
      return;
    }
    setJoinMode(true);
  }, [isAuthenticated, startCheckoutFlow]);

  const handleLoginSuccess = async (response) => {
    const { credential } = response;
    const shouldCheckoutAfterLogin = joinModeRef.current;
    let userTimezone = 'America/New_York';

    try {
      userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      // fallback above
    }

    if (shouldCheckoutAfterLogin) {
      setCheckoutLoading(true);
      setCheckoutError(null);
    }

    try {
      const res = await fetch(`${url}/record/googleLogin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token: credential,
          timezone: userTimezone,
          rememberMe: false,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || 'Login failed');
      }

      setIsAuthenticated?.(true);
      setUserId?.(data.user?._id ?? '');
      setRememberMeEligible(Boolean(data.access?.canUseRememberMe));
      await refreshAccess();

      if (shouldCheckoutAfterLogin) {
        await startCheckoutFlow();
        return;
      }

      closeSignInModal();
      onLoginSuccess?.(data);
    } catch (error) {
      console.error('Sign-in modal login failed:', error);
      if (shouldCheckoutAfterLogin) {
        setCheckoutError(formatJoinCheckoutError(error));
        setCheckoutLoading(false);
      }
    }
  };

  const handleGoogleError = useCallback(() => {
    if (joinModeRef.current) {
      setCheckoutError('Google sign-in failed. Please try again.');
      return;
    }
    console.error('Google sign-in failed');
  }, []);

  return (
    <Modal
      show={signInModalOpen}
      onHide={closeSignInModal}
      centered
      className="paywall-sign-in-modal"
      aria-labelledby="paywall-sign-in-modal-headline"
    >
      <Modal.Header closeButton className="paywall-sign-in-modal__header" />
      <Modal.Body className="text-center paywall-sign-in-modal__body">
        {joinMode ? (
          <>
            <h2 id="paywall-sign-in-modal-headline" className="paywall-sign-in-modal__headline">
              Join Calculus Circus
            </h2>
            <p className="paywall-sign-in-modal__subhead mb-3">
              Sign in with Google to continue to secure checkout ($30 for one-year, no auto-renewal).
            </p>
          </>
        ) : (
          <>
            <h2 id="paywall-sign-in-modal-headline" className="paywall-sign-in-modal__headline">
              {modalCopy.headline}
            </h2>
            <p className="paywall-sign-in-modal__subhead mb-3">
              {modalCopy.subhead}
            </p>
            {modalCopy.detail && (
              <p className="paywall-sign-in-modal__detail mb-3">
                {modalCopy.detail}
              </p>
            )}
          </>
        )}

        {checkoutError && (
          <Alert variant="danger" className="paywall-sign-in-modal__checkout-error text-start">
            {checkoutError}
            {checkoutError.includes('verify your email') && (
              <>
                {' '}
                <Button
                  variant="link"
                  className="p-0 align-baseline"
                  onClick={() => {
                    closeSignInModal();
                    navigate('/viewProfile');
                  }}
                >
                  Go to profile
                </Button>
              </>
            )}
          </Alert>
        )}

        <div className="paywall-sign-in-modal__google mb-3">
          {checkoutLoading ? (
            <div className="paywall-sign-in-modal__checkout-loading">
              <Spinner animation="border" role="status" className="me-2" />
              <span>Preparing checkout…</span>
            </div>
          ) : (
            <GoogleLogin
              key="paywall-sign-in-modal-google"
              onSuccess={handleLoginSuccess}
              onError={handleGoogleError}
              useOneTap={false}
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
            />
          )}
        </div>

        {joinMode ? (
          <>
            {!checkoutLoading && (
              <p className="paywall-sign-in-modal__manual-links mb-3">
                <button
                  type="button"
                  className="paywall-sign-in-modal__manual-link"
                  onClick={() => navigateToEmailAuth('/login/email')}
                >
                  Sign in with email
                </button>
                {' · '}
                <button
                  type="button"
                  className="paywall-sign-in-modal__manual-link"
                  onClick={() => navigateToEmailAuth('/register')}
                >
                  Create account
                </button>
              </p>
            )}
            <button
              type="button"
              className="paywall-sign-in-modal__skip"
              onClick={() => {
                setJoinMode(false);
                setCheckoutError(null);
              }}
              disabled={checkoutLoading}
            >
              Back to free trial options
            </button>
          </>
        ) : (
          <>
            <p className="paywall-sign-in-modal__or" aria-hidden="true">
              — or —
            </p>
            <p className="paywall-sign-in-modal__membership mb-3">
              Join Calculus Circus with a $30 one-year membership for full access.
            </p>
            <Button
              variant="warning"
              size="lg"
              className="paywall-sign-in-modal__join-btn mb-4"
              onClick={handleJoinClick}
              disabled={checkoutLoading}
            >
              {checkoutLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Preparing checkout…
                </>
              ) : (
                'Join the Circus!'
              )}
            </Button>
            <button
              type="button"
              className="paywall-sign-in-modal__skip"
              onClick={closeSignInModal}
            >
              Skip for now and continue with limited access.
            </button>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
